import { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { subscribeNewsletterMinimal } from "src/api/api";
import type { SxProps, Theme } from "@mui/material/styles";

export interface NewsletterSubscribeSectionProps {
  /** When false, newsletter is not rendered (e.g. embedded homepage uses a single block before FAQ). */
  show?: boolean;
  sx?: SxProps<Theme>;
}

export const NewsletterSubscribeSection: FC<
  NewsletterSubscribeSectionProps
> = ({ show = true, sx }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!show) {
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email.trim() || !name.trim()) {
      setError(t("Please provide both name and email."));
      return;
    }

    setIsLoading(true);
    try {
      const result = await subscribeNewsletterMinimal({
        name: name.trim(),
        email: email.trim(),
      });
      if (result.ok) {
        setMessage(result.message);
        setEmail("");
        setName("");
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error(err);
      setError(
        t("An error occurred while subscribing. Please try again later.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="section"
      aria-label="Newsletter signup"
      sx={[
        {
          py: { xs: 6, md: 8 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Container maxWidth="sm">
        <Typography variant="h4" component="h2" gutterBottom textAlign="center">
          {t("Join the email list")}
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          textAlign="center"
          sx={{ mb: 3 }}
        >
          {t("Subscribe to our newsletter.")}
        </Typography>

        {message ? (
          <Typography color="success.main" textAlign="center" variant="body1">
            {message}
          </Typography>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label={t("Name")}
                name="newsletter-name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                fullWidth
                required
                label={t("Email address")}
                name="newsletter-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? t("Subscribing…") : t("Subscribe")}
              </Button>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
};
